/**
 * Tab switcher for .tx-sp__tabs (single product Description / Downloads).
 *
 * Markup contract:
 *   <div class="tx-sp__tabs-list" role="tablist">
 *     <button class="tx-sp__tab" data-name="description" data-active="true"
 *             aria-controls="tx-sp__tp-description" …>…</button>
 *     <button class="tx-sp__tab" data-name="downloads" …>…</button>
 *   </div>
 *   <div class="tx-sp__tab-panel" id="tx-sp__tp-description">…</div>
 *   <div class="tx-sp__tab-panel" id="tx-sp__tp-downloads" hidden>…</div>
 */

const SELECTOR_LIST = '.tx-sp__tabs-list';
const SELECTOR_TAB  = '.tx-sp__tab';

const initTabs = (list) => {
    const tabs   = Array.from(list.querySelectorAll(SELECTOR_TAB));
    const card   = list.closest('.tx-sp__tabs-card') || list.parentElement;
    if (!card || tabs.length === 0) return;

    const panels = Array.from(card.querySelectorAll('.tx-sp__tab-panel'));

    const activate = (tab, focus = true) => {
        tabs.forEach((t) => {
            const selected = t === tab;
            t.setAttribute('aria-selected', selected ? 'true' : 'false');
            t.setAttribute('tabindex', selected ? '0' : '-1');
            if (selected) t.setAttribute('data-active', 'true');
            else t.removeAttribute('data-active');
        });
        panels.forEach((p) => {
            const targetId = tab.getAttribute('aria-controls');
            if (p.id === targetId) p.removeAttribute('hidden');
            else p.setAttribute('hidden', '');
        });
        if (focus) tab.focus();
    };

    list.addEventListener('click', (e) => {
        const btn = e.target.closest(SELECTOR_TAB);
        if (btn && tabs.includes(btn)) activate(btn);
    });

    list.addEventListener('keydown', (e) => {
        const i = tabs.indexOf(document.activeElement);
        if (i === -1) return;
        let next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activate(document.activeElement);
            return;
        } else return;
        e.preventDefault();
        if (next) next.focus();
    });
};

/**
 * Quantity stepper for .tx-sp__qty-control (single product page).
 */
const SELECTOR_QTY    = '.tx-sp__qty-control';
const SELECTOR_INPUT  = '.tx-sp__qty-input';
const SELECTOR_CART_QTY = '.woocommerce-cart .product-quantity .qty';

const parseQtyBound = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
};

const getQtyBounds = (input, fallbackMin = 1) => {
    const min = parseQtyBound(input.getAttribute('min'), fallbackMin);
    const max = parseQtyBound(input.getAttribute('max'), 99999);
    return { min, max: max < min ? 99999 : max };
};

const clampQty = (input, min, max) => {
    const current = parseInt(input.value || String(min), 10);
    return Math.max(min, Math.min(max, Number.isNaN(current) ? min : current));
};

const initQtyStepper = (control) => {
    const input = control.querySelector(SELECTOR_INPUT);
    if (!input) return;

    const minus = control.querySelector('.tx-sp__qty-btn--minus');
    const plus  = control.querySelector('.tx-sp__qty-btn--plus');
    const { min, max } = getQtyBounds(input);

    // Live TOTAL = unit-price × qty. The unit-price is on the control as
    // a data-unit-price attribute; the output element is the sibling
    // `.tx-sp__qty-total-value` somewhere in the order-console panel.
    const unitPrice = parseFloat(control.dataset.unitPrice || '0') || 0;
    const panel     = control.closest('.tx-sp__panel');
    const totalEl   = panel ? panel.querySelector('.tx-sp__qty-total-value') : null;

    const updateTotal = (qty) => {
        if (!totalEl || !unitPrice) return;
        const total = (unitPrice * qty).toFixed(2);
        // Format with thousands separators, $ prefix to match initial render.
        const [whole, dec] = total.split('.');
        const formatted = `$ ${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`;
        totalEl.textContent = formatted;
    };

    const step = (delta) => {
        const next = Math.max(min, Math.min(max, clampQty(input, min, max) + delta));
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    if (minus) minus.addEventListener('click', () => step(-1));
    if (plus)  plus.addEventListener('click',  () => step(+1));

    input.addEventListener('input', () => {
        updateTotal(clampQty(input, min, max));
    });
    input.addEventListener('change', () => {
        const v = clampQty(input, min, max);
        input.value = String(v);
        updateTotal(v);
    });
};

/**
 * Copy-to-clipboard for the order-console code row.
 */
const SELECTOR_COPY = '.tx-sp__copy[data-tx-copy]';

const writeToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }
    // http contexts (and older Safari) have no async clipboard.
    const holder = document.createElement('textarea');
    holder.value = text;
    holder.setAttribute('readonly', '');
    holder.style.position = 'fixed';
    holder.style.opacity = '0';
    document.body.appendChild(holder);
    holder.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(holder);
    return ok ? Promise.resolve() : Promise.reject(new Error('copy failed'));
};

const initCopyButton = (button) => {
    const label = button.querySelector('.tx-sp__copy-label');
    if (!label) return;

    const original = label.textContent;
    let timer = null;

    button.addEventListener('click', () => {
        writeToClipboard(button.dataset.txCopy || '')
            .then(() => { label.textContent = 'COPIED'; })
            .catch(() => { label.textContent = 'PRESS ⌘C'; })
            .then(() => {
                window.clearTimeout(timer);
                timer = window.setTimeout(() => { label.textContent = original; }, 1400);
            });
    });
};

/**
 * Quantity stepper for the WooCommerce cart table.
 */
const initCartQtyStepper = (input) => {
    if (!input || input.closest('.tx-cart__qty-control')) return;

    const { min, max } = getQtyBounds(input, 0);
    const control = document.createElement('div');
    const minus = document.createElement('button');
    const plus = document.createElement('button');

    control.className = 'tx-cart__qty-control';
    minus.type = 'button';
    minus.className = 'tx-cart__qty-btn tx-cart__qty-btn--minus clx-step';
    minus.setAttribute('aria-label', 'Decrease quantity');
    minus.textContent = '−';

    plus.type = 'button';
    plus.className = 'tx-cart__qty-btn tx-cart__qty-btn--plus clx-step';
    plus.setAttribute('aria-label', 'Increase quantity');
    plus.textContent = '+';

    input.classList.add('tx-cart__qty-input', 'mono');
    input.parentNode.insertBefore(control, input);
    control.append(minus, input, plus);

    const form = input.closest('form.woocommerce-cart-form');
    const updateButton = form ? form.querySelector('button[name="update_cart"]') : null;
    const enableUpdateCart = () => {
        if (!updateButton) return;
        updateButton.disabled = false;
        updateButton.removeAttribute('disabled');
    };
    const notifyCartChanged = () => {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        enableUpdateCart();
    };

    const step = (delta) => {
        const next = Math.max(min, Math.min(max, clampQty(input, min, max) + delta));
        input.value = String(next);
        notifyCartChanged();
    };

    minus.addEventListener('click', () => step(-1));
    plus.addEventListener('click', () => step(+1));
    input.addEventListener('input', enableUpdateCart);
    input.addEventListener('change', () => {
        input.value = String(clampQty(input, min, max));
        enableUpdateCart();
    });
};

const initCartQtySteppers = () => {
    document.querySelectorAll(SELECTOR_CART_QTY).forEach(initCartQtyStepper);
};

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(SELECTOR_LIST).forEach(initTabs);
    document.querySelectorAll(SELECTOR_QTY).forEach(initQtyStepper);
    document.querySelectorAll(SELECTOR_COPY).forEach(initCopyButton);
    initCartQtySteppers();

    if (window.jQuery) {
        window.jQuery(document.body).on('updated_wc_div', initCartQtySteppers);
    }
});
